import { type Client } from '@microsoft/microsoft-graph-client';
import type { ListName, SPListItem, ListIds } from '@/models/types';

const SP_HOSTNAME = import.meta.env.VITE_SP_HOSTNAME ?? '';
const SP_SITE_PATH = import.meta.env.VITE_SP_SITE_PATH ?? '';

let resolvedSiteId: string | null = null;
let resolvedListIds: Partial<ListIds> = {};

// ─── Site & List resolution ──────────────────────────────────────────────────

export async function resolveSiteId(client: Client): Promise<string> {
  if (resolvedSiteId) return resolvedSiteId;

  const site = await client
    .api(`/sites/${SP_HOSTNAME}:${SP_SITE_PATH}`)
    .select('id')
    .get();

  resolvedSiteId = site.id;
  return resolvedSiteId!;
}

export async function resolveListId(
  client: Client,
  listName: ListName,
): Promise<string> {
  if (resolvedListIds[listName]) return resolvedListIds[listName];

  const siteId = await resolveSiteId(client);
  const list = await client
    .api(`/sites/${siteId}/lists`)
    .filter(`displayName eq '${listName}'`)
    .select('id')
    .get();

  if (!list.value || list.value.length === 0) {
    throw new Error(`SharePoint list "${listName}" not found. Run the provisioning script first.`);
  }

  resolvedListIds[listName] = list.value[0].id;
  return resolvedListIds[listName]!;
}

export async function resolveAllListIds(client: Client): Promise<ListIds> {
  const siteId = await resolveSiteId(client);

  // Fetch all lists and filter client-side (more reliable than startsWith)
  const response = await client
    .api(`/sites/${siteId}/lists`)
    .select('id,displayName,name')
    .top(200)
    .get();

  const ids: Partial<ListIds> = {};
  for (const list of response.value) {
    const name = list.displayName || list.name;
    if (isOCList(name)) {
      ids[name as ListName] = list.id;
    }
  }

  console.info('[Prism] Resolved SharePoint lists:', Object.keys(ids));
  resolvedListIds = { ...resolvedListIds, ...ids };
  return resolvedListIds as ListIds;
}

function isOCList(name: string): name is ListName {
  return name.startsWith('OC_');
}

// ─── CRUD Operations ─────────────────────────────────────────────────────────

export async function getListItems<T>(
  client: Client,
  listName: ListName,
  options?: {
    filter?: string;
    select?: string[];
    top?: number;
    orderBy?: string;
  },
): Promise<SPListItem<T>[]> {
  const siteId = await resolveSiteId(client);
  const listId = await resolveListId(client, listName);

  let request = client
    .api(`/sites/${siteId}/lists/${listId}/items`)
    .expand('fields')
    // Allow non-indexed queries - always safe, helps with filter on boolean/choice fields
    .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly');

  if (options?.filter) request = request.filter(options.filter);
  if (options?.top) request = request.top(options.top);
  if (options?.orderBy) request = request.orderby(options.orderBy);

  const items: SPListItem<T>[] = [];
  let response;
  try {
    response = await request.get();
  } catch (err) {
    console.error(`[Prism] Failed to fetch ${listName}:`, err);
    throw err;
  }

  while (response) {
    for (const item of response.value) {
      items.push({ id: item.id, fields: item.fields });
    }

    if (response['@odata.nextLink']) {
      response = await client.api(response['@odata.nextLink']).get();
    } else {
      break;
    }
  }

  return items;
}

export async function getListItemById<T>(
  client: Client,
  listName: ListName,
  itemId: string,
): Promise<SPListItem<T>> {
  const siteId = await resolveSiteId(client);
  const listId = await resolveListId(client, listName);

  const item = await client
    .api(`/sites/${siteId}/lists/${listId}/items/${itemId}`)
    .expand('fields')
    .get();

  return { id: item.id, fields: item.fields };
}

export async function createListItem<T>(
  client: Client,
  listName: ListName,
  fields: Partial<T>,
): Promise<SPListItem<T>> {
  const siteId = await resolveSiteId(client);
  const listId = await resolveListId(client, listName);

  const item = await client
    .api(`/sites/${siteId}/lists/${listId}/items`)
    .post({ fields });

  return { id: item.id, fields: item.fields };
}

// ─── Schema bootstrapping ────────────────────────────────────────────────────

export interface CreateListColumn {
  name: string;
  /** One Graph column type. Pass exactly one of these populated. */
  type:
    | { text: { allowMultipleLines?: boolean } }
    | { number: { decimalPlaces?: 'none' | 'one' | 'two' | 'three'; minimum?: number; maximum?: number } }
    | { dateTime: object }
    | { choice: { choices: string[] } }
    | { boolean: object };
}

/**
 * Create a new SharePoint list with the given schema. Used by the admin
 * "Setup" buttons in the UI (Cost Allocation, Devices) so admins don't have
 * to drop into the SharePoint web UI or run a shell script.
 *
 * Caller must have `Sites.Manage.All` delegated scope (the app reg's
 * default `Sites.ReadWrite.All` is NOT enough to create lists). Idempotent:
 * if a list with the same displayName exists, returns its id.
 *
 * Will throw a friendly error if the user lacks permission so callers can
 * show a guidance message.
 */
export async function ensureListExists(
  client: Client,
  displayName: string,
  columns: CreateListColumn[],
): Promise<string> {
  const siteId = await resolveSiteId(client);

  // Check existence first — Graph 409s on duplicate displayName but the
  // error shape varies, easier to filter
  const existing = await client
    .api(`/sites/${siteId}/lists`)
    .filter(`displayName eq '${displayName}'`)
    .select('id')
    .get();
  if (existing.value?.length > 0) {
    return existing.value[0].id as string;
  }

  // Build the create payload — Graph schema for /sites/{id}/lists
  const payload = {
    displayName,
    list: { template: 'genericList' },
    columns: columns.map((c) => ({ name: c.name, ...c.type })),
  };

  try {
    const created = await client.api(`/sites/${siteId}/lists`).post(payload);
    // Invalidate the resolved-IDs cache so subsequent reads pick up the new list
    resolvedListIds = {};
    return created.id as string;
  } catch (err) {
    const e = err as { statusCode?: number; code?: string; message?: string };
    if (e.statusCode === 403 || e.code === 'accessDenied') {
      throw new Error(
        'Your account needs the "Sites.Manage.All" delegated Graph permission ' +
          'to create lists. Ask a Global Admin to grant admin consent for it ' +
          'on the OrgControl app registration.',
      );
    }
    throw err;
  }
}

export async function updateListItem<T>(
  client: Client,
  listName: ListName,
  itemId: string,
  fields: Partial<T>,
): Promise<void> {
  const siteId = await resolveSiteId(client);
  const listId = await resolveListId(client, listName);

  await client
    .api(`/sites/${siteId}/lists/${listId}/items/${itemId}/fields`)
    .patch(fields);
}

export async function deleteListItem(
  client: Client,
  listName: ListName,
  itemId: string,
): Promise<void> {
  const siteId = await resolveSiteId(client);
  const listId = await resolveListId(client, listName);

  await client
    .api(`/sites/${siteId}/lists/${listId}/items/${itemId}`)
    .delete();
}

// ─── Batch Operations ────────────────────────────────────────────────────────

export async function batchCreateItems<T>(
  client: Client,
  listName: ListName,
  itemsData: Partial<T>[],
): Promise<void> {
  const siteId = await resolveSiteId(client);
  const listId = await resolveListId(client, listName);
  const baseUrl = `/sites/${siteId}/lists/${listId}/items`;

  // Graph batch supports up to 20 requests per batch
  for (let i = 0; i < itemsData.length; i += 20) {
    const batch = itemsData.slice(i, i + 20);
    const batchContent = {
      requests: batch.map((fields, idx) => ({
        id: String(idx + 1),
        method: 'POST',
        url: baseUrl,
        headers: { 'Content-Type': 'application/json' },
        body: { fields },
      })),
    };

    await client.api('/$batch').post(batchContent);
  }
}

export async function batchUpdateItems<T>(
  client: Client,
  listName: ListName,
  updates: Array<{ itemId: string; fields: Partial<T> }>,
): Promise<void> {
  const siteId = await resolveSiteId(client);
  const listId = await resolveListId(client, listName);

  for (let i = 0; i < updates.length; i += 20) {
    const batch = updates.slice(i, i + 20);
    const batchContent = {
      requests: batch.map((update, idx) => ({
        id: String(idx + 1),
        method: 'PATCH',
        url: `/sites/${siteId}/lists/${listId}/items/${update.itemId}/fields`,
        headers: { 'Content-Type': 'application/json' },
        body: update.fields,
      })),
    };

    await client.api('/$batch').post(batchContent);
  }
}

export function clearCache() {
  resolvedSiteId = null;
  resolvedListIds = {};
}
